import {
  SlidersHorizontal, Clock3, MailWarning, Users, MessageSquare,
  ArrowRight, CheckCircle2, Building2, Home, CalendarClock,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';

const Section = ({ icon: Icon, tint, title, testid, children }) => (
  <section
    className="rounded-lg border border-border bg-card overflow-hidden"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
      <span className={`flex h-9 w-9 items-center justify-center rounded-md ${tint}`}>
        <Icon className="h-4 w-4" />
      </span>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
    <div className="px-4 py-4 text-sm text-muted-foreground space-y-3 leading-relaxed">
      {children}
    </div>
  </section>
);

const Step = ({ n, children }) => (
  <div className="flex gap-3">
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-bold">
      {n}
    </span>
    <p className="text-sm text-foreground/80">{children}</p>
  </div>
);

export function MissingBillsHelpDrawer({ open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
        data-testid="mb-help-drawer"
      >
        <div className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <MailWarning className="h-5 w-5 text-primary" /> How Missing Bills works
            </SheetTitle>
            <SheetDescription>
              A quick guide to setting it up, reading the two states, and working
              on it together as a team.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* SETUP */}
          <Section
            icon={SlidersHorizontal}
            tint="bg-indigo-100 text-indigo-700"
            title="Setting it up"
            testid="mb-help-setup"
          >
            <p>
              Missing Bills starts counting from your <strong>Missing Bill Start
              Date</strong>. Periods before that date are treated as historical
              and left out of the client-facing counts, so old gaps don't alarm
              anyone.
            </p>
            <div className="space-y-2.5 pt-1">
              <Step n={1}>
                Go to <strong>Settings → Missing Bills</strong>.
              </Step>
              <Step n={2}>
                Set the <strong>org-level</strong> start date (defaults to your
                organisation's creation date), and optionally a{' '}
                <strong>per-account</strong> start date to override individual
                accounts that came on board later.
              </Step>
              <Step n={3}>
                As you type a new date you'll see a live{' '}
                <strong>"X → Y bills"</strong> preview of the impact. Saving
                applies it and recomputes the list <strong>instantly</strong> —
                no waiting for an overnight job.
              </Step>
            </div>
            <div className="flex items-start gap-2 rounded-md bg-muted/40 border border-border px-3 py-2 mt-2">
              <Home className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span className="text-xs">
                The <strong>"Compute missing bills for units"</strong> toggle
                (also in Settings) decides whether unit-level bills are tracked
                alongside common-area bills.
              </span>
            </div>
          </Section>

          {/* LATE vs MISSING */}
          <Section
            icon={Clock3}
            tint="bg-amber-100 text-amber-700"
            title="Late Arriving vs Missing"
            testid="mb-help-states"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3">
                <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                  <Clock3 className="h-4 w-4" /> Late Arriving
                </div>
                <p className="mt-2 text-xs text-amber-900/80 leading-relaxed">
                  A bill we normally receive on a set schedule is overdue but
                  still <strong>expected</strong>. The account has a known
                  billing cadence, so it's most likely still on its way.
                </p>
              </div>
              <div className="rounded-md border border-rose-200 bg-rose-50/70 p-3">
                <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm">
                  <MailWarning className="h-4 w-4" /> Missing
                </div>
                <p className="mt-2 text-xs text-rose-900/80 leading-relaxed">
                  A billing period has passed with <strong>no bill and no sign
                  it's coming</strong> through the usual channels — this one
                  needs to be tracked down.
                </p>
              </div>
            </div>
            <p className="pt-1">
              Each missing billing period is its own row. The <strong>day
              badge</strong> shows how overdue it is — <span className="font-medium text-amber-700">amber</span> is
              getting late, <span className="font-medium text-rose-700">red</span> is
              well past due and worth chasing first.
            </p>
            <p className="text-xs">
              Units with no bill for 45+ days auto-flip to <strong>occupied</strong>,
              and occupied units stop being flagged 30 days after occupancy — so
              vacant-unit noise clears itself.
            </p>
          </Section>

          {/* WHEN LATE BECOMES MISSING */}
          <Section
            icon={CalendarClock}
            tint="bg-violet-100 text-violet-700"
            title="When Late-Arriving becomes Missing"
            testid="mb-help-threshold"
          >
            <p>
              Decide how long a bill stays <span className="text-amber-700 font-medium">Late
              Arriving</span> before it flips to <span className="text-rose-700 font-medium">Missing
              (overdue)</span> in <strong>Settings → Missing Bills → "Late-Arriving → Missing
              after (days)"</strong>.
            </p>
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
              <strong>Example:</strong> a bill expected on <strong>06 Aug</strong> with a{' '}
              <strong>4-day</strong> threshold stays Late Arriving on 06–09 Aug, then shows as{' '}
              <strong>Missing on 10 Aug</strong> (4 days past the expected date).
            </div>
            <p className="text-xs">
              Leave it blank to use the default — only the most recent overdue bill stays
              Late Arriving and everything older is Missing. Changing it recomputes the list
              instantly.
            </p>
          </Section>

          {/* TEAM */}
          <Section
            icon={Users}
            tint="bg-emerald-100 text-emerald-700"
            title="Working as a team"
            testid="mb-help-team"
          >
            <p>
              Split the workload by assigning rows to teammates:
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                <span>
                  Use the <strong>Assigned To</strong> control on any row to hand
                  it to a colleague (or grab it yourself).
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                <span>
                  Filter with the <strong>Assigned To</strong> chip at the top to
                  focus on <strong>My Queue</strong> and see only what's yours.
                </span>
              </div>
            </div>
          </Section>

          {/* NOTES */}
          <Section
            icon={MessageSquare}
            tint="bg-sky-100 text-sky-700"
            title="Leaving notes"
            testid="mb-help-notes"
          >
            <p>
              Open the <strong>notes</strong> icon on any row to leave context
              the whole team can see — e.g.{' '}
              <em>"Emailed the vendor on the 3rd, waiting on a re-send."</em>
            </p>
            <p className="flex items-center gap-1.5 text-xs">
              Notes travel with the row <ArrowRight className="h-3.5 w-3.5" /> so
              whoever picks it up next has the full history, no hand-off needed.
            </p>
          </Section>

          <p className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <Building2 className="h-3.5 w-3.5" />
            Both common-area and unit accounts are covered wherever unit tracking
            is enabled.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default MissingBillsHelpDrawer;
